import React from 'react';
import { Copy, Edit } from 'lucide-react';

/**
 * MainRow
 * Displays the parent-level row for each user_code.
 * Supports edit and view mode.
 *
 * Props:
 * - userDetails: { userCode, fullName, slackId, referral, crm_password, notes, subscribed, onboarded, totalEntries }
 * - isExpanded: bool
 * - isEditing: bool
 * - isNotesOpen: bool
 * - hasNotes: bool
 *
 * Editing:
 * - mainRowEditDraft
 * - onMainRowFieldChange(field, value)
 * - onSaveMainRow(userCode)
 * - onCancelMainRow()
 *
 * View:
 * - onToggleExpand(userCode)
 * - onToggleNotes(userCode)
 * - onStartEditingMainRow(userDetails)
 *
 * Misc:
 * - onCopy(text)
 * - showMultilogin (bool)
 */
export default function MainRow({
  userDetails,
  isExpanded,
  isEditing,
  isNotesOpen,
  hasNotes,

  mainRowEditDraft,
  onMainRowFieldChange,
  onSaveMainRow,
  onCancelMainRow,

  onToggleExpand,
  onToggleNotes,
  onStartEditingMainRow,
  onCopy,

  showMultilogin = false,
}) {
  const {
    userCode,
    fullName,
    slackId,
    referral,
    crm_password,
    notes,
    subscribed,
    onboarded,
    totalEntries,
  } = userDetails;

  if (isEditing) {
    return (
      <tr>
        <td>{userCode}</td>

        {/* Full Name */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm"
            value={mainRowEditDraft.fullName}
            onChange={(e) => onMainRowFieldChange('fullName', e.target.value)}
          />
        </td>

        {/* Multilogin toggle */}
        {showMultilogin && (
          <td>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                checked={!!mainRowEditDraft.multilogin}
                onChange={(e) =>
                  onMainRowFieldChange('multilogin', e.target.checked)
                }
              />
              <label className="form-check-label">Multi</label>
            </div>
          </td>
        )}

        {/* Subscribed / Onboarded (badges only in edit mode) */}
        <td>
          <span className={`badge ${subscribed ? 'bg-success' : 'bg-secondary'}`}>
            Subscribed
          </span>
        </td>
        <td>
          <span className={`badge ${onboarded ? 'bg-success' : 'bg-secondary'}`}>
            Onboarded
          </span>
        </td>

        {/* Slack ID */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm"
            value={mainRowEditDraft.slackId}
            onChange={(e) => onMainRowFieldChange('slackId', e.target.value)}
          />
        </td>

        {/* Total entries */}
        <td>{totalEntries}</td>

        {/* CRM password */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm"
            value={mainRowEditDraft.crm_password}
            onChange={(e) =>
              onMainRowFieldChange('crm_password', e.target.value)
            }
          />
        </td>

        {/* Referral */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm"
            value={mainRowEditDraft.referral}
            onChange={(e) => onMainRowFieldChange('referral', e.target.value)}
          />
        </td>

        {/* Actions */}
        <td>
          <button
            className="btn btn-primary btn-sm me-2"
            onClick={() => onSaveMainRow(userCode)}
          >
            Save
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={onCancelMainRow}
          >
            Cancel
          </button>
        </td>
      </tr>
    );
  }

  // ----- View mode -----
  const notesButtonClass = hasNotes
    ? 'btn btn-primary btn-sm'
    : 'btn btn-outline-primary btn-sm';

  return (
    <tr
      className={`cursor-pointer ${isExpanded ? 'expanded-parent' : ''}`}
      onClick={() => onToggleExpand(userCode)}
    >
      <td>{userCode}</td>
      <td>{fullName}</td>

      {/* Multilogin */}
      {showMultilogin && (
        <td>
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input"
              type="checkbox"
              checked={!!userDetails.multilogin}
              readOnly
            />
          </div>
        </td>
      )}

      {/* Subscribed */}
      <td>
        <span className={`badge ${subscribed ? 'bg-success' : 'bg-secondary'}`}>
          Subscribed
        </span>
      </td>

      {/* Onboarded */}
      <td>
        <span className={`badge ${onboarded ? 'bg-success' : 'bg-secondary'}`}>
          Onboarded
        </span>
      </td>

      {/* Slack ID */}
      <td>
        {slackId && (
          <>
            {slackId}{' '}
            <button
              className="btn btn-link btn-sm p-0 ms-1"
              onClick={(e) => {
                e.stopPropagation();
                onCopy?.(slackId);
              }}
            >
              <Copy size={14} />
            </button>
          </>
        )}
      </td>

      {/* Total entries */}
      <td>{totalEntries}</td>

      {/* CRM password */}
      <td>
        {crm_password && (
          <>
            {crm_password}{' '}
            <button
              className="btn btn-link btn-sm p-0 ms-1"
              onClick={(e) => {
                e.stopPropagation();
                onCopy?.(crm_password);
              }}
            >
              <Copy size={14} />
            </button>
          </>
        )}
      </td>

      {/* Referral */}
      <td>{referral}</td>

      {/* Actions */}
      <td>
        <button
          className={notesButtonClass}
          onClick={(e) => {
            e.stopPropagation();
            onToggleNotes(userCode);
          }}
        >
          Notes
        </button>
        <button
          className="btn btn-outline-secondary btn-sm ms-2"
          onClick={(e) => {
            e.stopPropagation();
            onStartEditingMainRow(userDetails);
          }}
        >
          <Edit size={14} />
        </button>
      </td>
    </tr>
  );
}
